/**
 * Phase 38 — "Hit again": a per-DROP verdict. When a sent drop's own settled
 * stats satisfy the rule, the engine sends again RIGHT NOW to the same list,
 * on the same gateway, with a changed amount — up to N times along the
 * chain (a hit-again drop is a drop: it is judged too). These are the
 * action's settings and the report shapes; the rule itself is a gate tree.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { GateConditionResult } from "../../gate-rules/gate-rule.types";

/** How the new drop's amount is made. */
export const DROP_GEN_HIT_AMOUNT_MODES = ["same", "drop_pct", "base_pct", "fixed"] as const;
export type DropGenHitAmountMode = (typeof DROP_GEN_HIT_AMOUNT_MODES)[number];

export const DROP_GEN_HIT_AMOUNT_WORDS: Record<DropGenHitAmountMode, string> = {
  same: "the same amount as the drop",
  drop_pct: "% of the drop that hit",
  base_pct: "% of the base amount",
  fixed: "a fixed amount",
};

export const DROP_GEN_HIT_LIMITS = {
  /** +X % may be negative (send less) and at most five times more. */
  pct: { min: -90, max: 500 },
  amount: { min: 1, max: 1_000_000 },
  /** When a limit is set: how many times one drop's chain may hit again a day. */
  maxTimes: { min: 1, max: 20 },
} as const;

/** 18/10/2026 — when a hit-again drop may go out: any time the country window is
 *  open (WINDOW, the default — after the container's last drop too), or only
 *  between the container's first and last drop of the day (CONTAINER). */
export const DROP_GEN_HIT_WHEN = ["WINDOW", "CONTAINER"] as const;
export type DropGenHitWhen = (typeof DROP_GEN_HIT_WHEN)[number];

/** The action as the satellite stores it. */
export interface DropGenHitAction {
  mode: DropGenHitAmountMode;
  /** For drop_pct / base_pct. */
  pct: number;
  /** For fixed. */
  fixed: number | null;
  /** The new amount never goes over this; null = no cap. */
  cap: number | null;
  /** 20/10/2026 — null = no limit (the default: every drop that hits is sent again, the
   *  hit-again drops too); N = the chain stops after N hits (1 = the original hits once,
   *  its hit-again drop never does). */
  maxTimes: number | null;
}

export const DEFAULT_HIT_ACTION: DropGenHitAction = { mode: "drop_pct", pct: 25, fixed: null, cap: null, maxTimes: null };

/** One hit decided by a pass. */
export interface DropGenHitDecision {
  sourceDropId: string;
  /** 1 = the original drop's hit, 2 = its hit-again drop's hit, … */
  hitNo: number;
  listName: string | null;
  /** The drop that hit sent this many. */
  from: number;
  amount: number;
  /** "+25 % of 1,000". */
  note: string;
  /** The recipient-local minute of the run day the new drop was laid at. */
  minute: number;
  conditions: GateConditionResult[];
}

/** A settled drop the rule held on, but nothing was sent — and why. */
export interface DropGenHitHeld {
  sourceDropId: string;
  listName: string | null;
  why: string;
}

/** Per container, on the day's verdict report. */
export interface DropGenHitReport {
  containerId: string;
  containerName: string;
  /** Settled sent drops the rule was judged on. */
  judged: number;
  /** The day's hits so far (22/10/2026 — the Live card's "×N today"), not only this pass's. */
  hits: DropGenHitDecision[];
  held: DropGenHitHeld[];
}
